import { Injectable } from '@angular/core';
import { DataService } from '../data/data.service';
import { IContractData } from '../../interfaces/IJob';
import { IBookingConfirmationEvent, IPurchase, IPurchaseEventItem, IPurchaseEventPayload, ISendPageView, ItemSelectionWay } from './ga4.interface';
import { ACC_LOGGEDIN, ACC_LOGGEDOUT, ACC_VIVR, ALL_PAGES, CONFIRMATION, CREDIT_CARD, CURRENCY_USD, ERR_EVENT, EVENT_BUTTON_CLICK, FFS_PLAN_TYPE, HIT_TYPE, JOB_BOOKING_CONFIRMATION_EVENT, LANDING_PAGE_CROSS_SELL_CE_FROM_DASHBOARD, LANDING_PAGE_CROSS_SELL_CE_FROM_MY_PLAN, LANDING_PAGE_CROSS_SELL_CE_FROM_SR_CONFRIMATION, LANDING_PAGE_CROSS_SELL_CE_FROM_SR_CONFRIMATION_VIVR, MY_PLAN_STORY_FEEDBACK_EVENT, MYACCOUNT_LOGGEDIN_SR, OTHER_LABEL, PAGE_VIEW, PLAN_DETAILS, PURCHASE, TEMPLATE_COMPLETE, UNDEFINED_USERID, USER_COUNTRY } from './ga4.constant';
import { UtilitiesService } from '../utilities/utilities.service';
import { CLIENT_ID } from '../../utils/constants';
// declare const dataLayer: Object[];
declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class Ga4Service {
  private cachedUserId: string | null = null;
  private cachedSiteName: string | null = null;

  constructor(
    private dataService: DataService,
    private utilService: UtilitiesService
  ) {
    this.initializeCachedValues();
  }
  private initializeCachedValues(): void {
    // 1. Authenticated Auth0 user ID
    this.dataService.auth0UserId.subscribe(
      (userId) => (this.cachedUserId = userId)
    );

    this.dataService.siteName$.subscribe(
      (siteName) => (this.cachedSiteName = siteName)
    );
  }

  /**
   * Resolves the user identifier:
   * 1. Primary: Auth0 user ID (for standard authenticated users)
   * 2. Fallback: VIVR source identifier (e.g., 'IVR-100234567' for VIVR users)
   * 3. Undefined: if completely anonymous/guest
   */
  private getEffectiveUserId(): string {
    if (this.cachedUserId) {
      return this.cachedUserId;
    }
    if (this.dataService.isVivr$.getValue()) {
      return this.dataService.vivrSub$.getValue() || UNDEFINED_USERID;
    }
    return UNDEFINED_USERID;
  }

  private createPageViewData(
    pageName: string,
    contractData: IContractData
  ): ISendPageView {
    const { baseContract } = contractData;
    const { coverageGroup = '', clientId = '' } = baseContract || {};

    return {
      event: PAGE_VIEW,
      hit_type: HIT_TYPE,
      page_name: pageName,
      template_name: ALL_PAGES,
      user_id: this.getEffectiveUserId(),
      user_site: this.cachedSiteName && this.cachedSiteName !== 'null' ? this.cachedSiteName : OTHER_LABEL,
      user_country: USER_COUNTRY,
      coverage_group: coverageGroup,
      cinch_client_id: clientId,
    };
  }

  sendPageView(pageName: string | undefined): void {
    try {
      if (!pageName) {
        console.warn('GA4Service: Page name is required');
        return;
      }

      const contractData = this.dataService.contractData$.getValue();
      if (!contractData) {
        console.warn('GA4Service: Contract data is required');
        return;
      }

      const pageViewData = this.createPageViewData(pageName, contractData);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(pageViewData);

    } catch (error) {
      console.error('GA4Service: Error sending page view:', error);
    }
  }

  pushEvent(event: string, data: Record<string, any> = {}): void {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event,
      user_id: this.getEffectiveUserId(),
      ...data
    });
  }

  buttonClickEvent(
    buttonLocation: string,
    buttonName: string,
    buttonText: string | null = null
  ): void {
    if (buttonText) {
      this.pushEvent(EVENT_BUTTON_CLICK, {
        button_location: buttonLocation,
        button_name: buttonName,
        button_text: buttonText,
        template_name: ALL_PAGES,

      });
    } else {
      this.pushEvent(EVENT_BUTTON_CLICK, {
        button_location: buttonLocation,
        button_name: buttonName,
        template_name: ALL_PAGES,

      });
    }
  }

  apiEvent(event: string,
    templateName: string,
  ): void {
    this.pushEvent(event, {
      template_name: templateName,
    });
  }

  messageEvent(event: string, errorShown: string, templateName: string = ALL_PAGES): void {
    const sanitizedErr = this.utilService.sanitizeErrorMessage(errorShown);
    this.pushEvent(event, {
      error_shown: sanitizedErr,
      template_name: templateName,
    });
  }

  cancelPlanEvent(
    event: string, amountDue: number, refundDue: number, cancelReason: string, templateName: string): void {
    this.pushEvent(event, {
      template_name: templateName,
      cancel_plan_amount_due: amountDue,
      cancel_plan_refund_due: refundDue,
      cancellation_reason: cancelReason
    });
  }

  sendSrFlowEvent(
    event: string,
    planType: string,
    userFunnel: string = MYACCOUNT_LOGGEDIN_SR,
    isRecall: boolean = false,
    hasModel?: boolean,
    hasSerial?: boolean,
    itemSelectionWay: ItemSelectionWay | null = null,
  ): void {
    this.pushEvent(event, {
      template_name: TEMPLATE_COMPLETE,
      user_funnel: userFunnel,
      PlanType: this.dataService.isPlanFFS() ? FFS_PLAN_TYPE : planType,
      isRecall: isRecall,
      Has_Model: hasModel ? 'YES' : 'NO',
      Has_Serial: hasSerial ? 'YES' : 'NO',
      Item_Selection_way: isRecall ? 'Recall_Item' : (itemSelectionWay ?? null),
    });
  }

  purchaseEvent(
    payload: IPurchaseEventPayload,
    items: IPurchaseEventItem[] = [],
    transactionType: string,
    planType: string,
    isRecall: boolean = false,
    noDeductable = false,
    transactionId: string | null = null,
    user_funnel: string = this.cachedUserId ? ACC_LOGGEDIN : ACC_LOGGEDOUT,
    paymentMethod: string | null = CREDIT_CARD,
  ): void {
    const computedUserFunnel = user_funnel ?? (this.cachedUserId ? ACC_LOGGEDIN : ACC_LOGGEDOUT);
    const computedPaymentMethod = !noDeductable
      ? ((paymentMethod && paymentMethod.trim()) || CREDIT_CARD)
      : null;
    const data: IPurchase = {
      event: PURCHASE.label,
      cell_code: payload.cellCode,
      currency: CURRENCY_USD,
      value: payload.value,
      hit_type: PURCHASE.hitType,
      payment_method: computedPaymentMethod,
      user_id: this.getEffectiveUserId(),
      user_funnel: computedUserFunnel,
      template_name: CONFIRMATION,
      PlanType: this.dataService.isPlanFFS() ? FFS_PLAN_TYPE : planType,
      coverageGroup: payload.coverageGroup,
      isRecall: isRecall,
      ecommerce: {
        tax: null,
        transaction_id: transactionId,
        transaction_type: transactionType,
        items,
      },
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'ecommerce': null });
    window.dataLayer.push(data)
  }

  vivrPurchaseEvent(
    payload: IPurchaseEventPayload,
    items: IPurchaseEventItem[] = [],
    transactionType: string,
    planType: string,
    paymentMethod: string | null = CREDIT_CARD,
    isRecall: boolean = false,
    transactionId: string | null = null,
    user_funnel: string = this.cachedUserId ? ACC_LOGGEDIN : ACC_LOGGEDOUT,
  ): void {
    const computedUserFunnel = user_funnel ?? (this.cachedUserId ? ACC_LOGGEDIN : ACC_LOGGEDOUT);
    const data: IPurchase = {
      event: PURCHASE.label,
      cell_code: payload.cellCode,
      currency: CURRENCY_USD,
      value: payload.value,
      hit_type: PURCHASE.hitType,
      payment_method: paymentMethod,
      user_id: this.getEffectiveUserId(),
      user_funnel: computedUserFunnel,
      template_name: CONFIRMATION,
      PlanType: this.dataService.isPlanFFS() ? FFS_PLAN_TYPE : planType,
      coverageGroup: payload.coverageGroup,
      isRecall: isRecall,
      ecommerce: {
        tax: null,
        transaction_id: transactionId,
        transaction_type: transactionType,
        items,
      },
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'ecommerce': null });
    window.dataLayer.push(data)
  }

  /**
  * @description Pass the details to DataLayer while its Checkout Begin event .
  * @param payload
  * @param items
  */
  bookingConfirmationEvent(
    payload: IPurchaseEventPayload,
    items: IPurchaseEventItem[],
    planType: string,
    isRecall: boolean,
    transaction_type: string,
    user_funnel: string = this.cachedUserId ? ACC_LOGGEDIN : ACC_LOGGEDOUT,
    hasModel?: boolean,
    hasSerial?: boolean,
    itemSelectionWay?: ItemSelectionWay | null,
  ): void {
    const data: IBookingConfirmationEvent = {
      event: JOB_BOOKING_CONFIRMATION_EVENT,
      cell_code: payload.cellCode,
      currency: CURRENCY_USD,
      value: payload.value,
      template_name: PLAN_DETAILS,
      user_id: this.getEffectiveUserId(),
      user_funnel: user_funnel,
      coverageGroup: payload.coverageGroup,
      isRecall: isRecall,
      PlanType: this.dataService.isPlanFFS() ? FFS_PLAN_TYPE : planType,
      items_count: items.length,
      ecommerce: {
        items,
        tax: null,
        transaction_id: null,
        transaction_type: transaction_type
      },
      Has_Model: hasModel ? 'YES' : 'NO',
      Has_Serial: hasSerial ? 'YES' : 'NO',
      Item_Selection_way: isRecall ? 'Recall_Item' : (itemSelectionWay ?? null),
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data)
  }

  myPlanStoryFeedbackEvent(
    reaction: 'up' | 'down',
    storySource: 'ai' | 'fallback',
    scenario: string,
    templateName: string = ALL_PAGES,
  ): void {
    this.pushMyPlanStoryEvent(MY_PLAN_STORY_FEEDBACK_EVENT, storySource, scenario, templateName, { reaction });
  }

  private pushMyPlanStoryEvent(
    event: string,
    storySource: 'ai' | 'fallback',
    scenario: string,
    templateName: string,
    extra: Record<string, any> = {},
  ): void {
    this.pushEvent(event, {
      ...extra,
      story_source: storySource,
      scenario,
      template_name: templateName,
    });
  }

  setGa4DtcSearsLanding(buttonName: string, clientName: string = '', isMessageShownEvent: boolean = false): void {
    const source = sessionStorage.getItem('source');
    let sourceLocation = LANDING_PAGE_CROSS_SELL_CE_FROM_DASHBOARD;

    switch (source) {
      case 'vivr':
        sourceLocation = LANDING_PAGE_CROSS_SELL_CE_FROM_SR_CONFRIMATION_VIVR;
        break;
      case 'srConfirmation':
        sourceLocation = LANDING_PAGE_CROSS_SELL_CE_FROM_SR_CONFRIMATION;
        break;
      case 'myPlan':
        sourceLocation = LANDING_PAGE_CROSS_SELL_CE_FROM_MY_PLAN;
        break;
      default:
        sourceLocation = LANDING_PAGE_CROSS_SELL_CE_FROM_DASHBOARD;
    }
    if (clientName.toUpperCase() === CLIENT_ID.SEARS) {
      sourceLocation = 'Sears_' + sourceLocation;
      buttonName = 'Sears_' + buttonName;
    } else {
      sourceLocation = 'DTC_' + sourceLocation;
      buttonName = 'DTC_' + buttonName;
    }
    if (isMessageShownEvent) {
      this.messageEvent(ERR_EVENT, buttonName, ALL_PAGES);
    } else {
      this.buttonClickEvent(sourceLocation, buttonName);
    }
  }
}
